import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { adminAPI } from '../utils/api';

interface DashboardStats {
    totalUsers: number;
    totalServers: number;
    totalComments: number;
}

const AdminStatistics: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const data = await adminAPI.getStats();
                setStats(data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch statistics:', err);
                setError(t('admin.stats.error_loading') || 'Failed to load statistics');
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [t]);

    const formatNumber = (num: number): string => {
        return num.toLocaleString();
    };

    const statsData = [
        {
            label: t('admin.total_users'),
            value: loading ? '...' : formatNumber(stats?.totalUsers || 0),
            color: 'text-purple-400'
        },
        {
            label: t('admin.stats.total_servers'),
            value: loading ? '...' : formatNumber(stats?.totalServers || 0),
            color: 'text-blue-400'
        },
        {
            label: t('admin.total_comments'),
            value: loading ? '...' : formatNumber(stats?.totalComments || 0),
            color: 'text-yellow-400'
        },
        {
            label: t('admin.active_sessions') || 'Active Sessions',
            value: loading ? '...' : '-',
            color: 'text-green-400'
        },
    ];

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                    {t('admin.stats.title')}
                </h1>
                <button
                    onClick={() => navigate('/admin')}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                    {t('admin.back_to_dashboard')}
                </button>
            </div>

            {error && (
                <div className="bg-red-900/50 border border-red-700 text-red-200 p-4 rounded-lg mb-6">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statsData.map((stat, index) => (
                    <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                        <div className={`text-3xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                        <div className="text-gray-400">{stat.label}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 h-64 flex items-center justify-center">
                    <p className="text-gray-500">{t('admin.stats.chart.user_growth')} (Placeholder)</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 h-64 flex items-center justify-center">
                    <p className="text-gray-500">{t('admin.stats.chart.server_activity')} (Placeholder)</p>
                </div>
            </div>
        </div>
    );
};

export default AdminStatistics;
